import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useTeamStore } from "@/store/useTeamStore";
import { displayAssistantName } from "@/lib/personaSamples";

// Phase 8b — Persona store
// Single source of truth for assistant identity (name, tagline, pronouns, avatar),
// voice samples, tone sliders, and per-channel signatures.
//
// IMPORTANT: setName() ALSO mirrors into useTeamStore.assistantName so every existing
// useAssistantName() selector across the app reactively updates (Today greeting,
// inbox composer hint, Studio, tour, "Ask Maya..." buttons, etc.).

const DEFAULT_SAMPLES = {
  writingSamples: "",        // textarea: DMs / posts
  audioSampleName: null,     // mock file name
  longFormSample: "",        // essay
};

const DEFAULT_TONES = {
  formalCasual: 60,    // 0=formal · 100=casual; default warm
  directDiplomatic: 30,// 0=direct · 100=diplomatic; default 30 = mostly direct
  conciseDetailed: 35, // 0=concise · 100=detailed; default 35 = mostly concise
  confidentHumble: 40, // 0=confident · 100=humble; default 40 = leans confident
  wittyEarnest: 45,    // 0=witty · 100=earnest; default 45 = balanced, slight witty
};

const DEFAULT_SIGNATURES = {
  email: "Aman Mehra · Founder, Sellyx · sellix.ai · aman@sellix.ai",
  linkedin: "Aman · Sellyx",
  whatsappEnabled: false,
};

export { displayAssistantName } from "@/lib/personaSamples";

export const usePersonaStore = create(
  persist(
    (set, get) => ({
      name: "Maya",
      tagline: "Your AI SDR",
      pronouns: "she/her",
      avatarKind: "gradient", // "gradient" | "uploaded" | "remote"
      avatarLabel: null,
      /** Display URL: authenticated blob URL from API, or local data: draft while editing. */
      avatarPreviewUrl: null,
      /** True when user cleared avatar in this edit session (persisted on Save). */
      avatarPendingClear: false,
      samples: { ...DEFAULT_SAMPLES },
      tones: { ...DEFAULT_TONES },
      signatures: { ...DEFAULT_SIGNATURES },

      setName: (name) => {
        const raw = name ?? "";
        set({ name: raw });
        useTeamStore.setState({ assistantName: displayAssistantName(raw) });
      },
      setTagline: (tagline) => set({ tagline: tagline ?? "" }),
      setPronouns: (pronouns) => set({ pronouns: pronouns || "they/them" }),
      setAvatar: (kind, label) => set({ avatarKind: kind, avatarLabel: label }),
      restoreAvatar: (snapshot) => {
        if (!snapshot || typeof snapshot !== "object") return;
        set({
          avatarKind: snapshot.avatarKind || "gradient",
          avatarLabel: snapshot.avatarLabel ?? null,
          avatarPreviewUrl: snapshot.avatarPreviewUrl ?? null,
          avatarPendingClear: Boolean(snapshot.avatarPendingClear),
        });
      },
      snapshotAvatar: () => {
        const s = get();
        return {
          avatarKind: s.avatarKind,
          avatarLabel: s.avatarLabel,
          avatarPreviewUrl: s.avatarPreviewUrl,
          avatarPendingClear: s.avatarPendingClear,
        };
      },
      hasCustomAvatar: () => {
        const s = get();
        if (s.avatarPendingClear) return false;
        return s.avatarKind === "uploaded" || s.avatarKind === "remote" || Boolean(s.avatarPreviewUrl);
      },
      clearAvatar: () => {
        set({
          avatarKind: "gradient",
          avatarLabel: null,
          avatarPreviewUrl: null,
          avatarPendingClear: true,
        });
      },
      /** Apply persona GET/PATCH/upload response (backend is source of truth). */
      applyServerAvatar: (persona, objectUrl) => {
        if (persona?.has_avatar && objectUrl) {
          set({
            avatarKind: "remote",
            avatarLabel: null,
            avatarPreviewUrl: objectUrl,
            avatarPendingClear: false,
          });
          return;
        }
        set({
          avatarKind: "gradient",
          avatarLabel: null,
          avatarPreviewUrl: null,
          avatarPendingClear: false,
        });
      },
      setAvatarFromFile: (file) => {
        if (!file) return;
        if (!String(file.type || "").startsWith("image/")) {
          return { ok: false, error: "Please choose an image file (PNG, JPG, or WebP)." };
        }
        if (file.size > 5 * 1024 * 1024) {
          return { ok: false, error: "Image must be 5 MB or smaller." };
        }
        const reader = new FileReader();
        reader.onload = () => {
          set({
            avatarKind: "uploaded",
            avatarLabel: file.name,
            avatarPreviewUrl: typeof reader.result === "string" ? reader.result : null,
            avatarPendingClear: false,
          });
        };
        reader.onerror = () => {
          set({
            avatarKind: "gradient",
            avatarLabel: null,
            avatarPreviewUrl: null,
            avatarPendingClear: false,
          });
        };
        reader.readAsDataURL(file);
        return { ok: true };
      },

      setSample: (key, value) => set((s) => ({ samples: { ...s.samples, [key]: value } })),
      setTone: (key, value) => set((s) => ({ tones: { ...s.tones, [key]: value } })),
      setSignature: (key, value) => set((s) => ({ signatures: { ...s.signatures, [key]: value } })),

      hydrateFromServer: (persona) => {
        if (!persona) return;
        const clean = (persona.assistant_name || "").trim();
        if (!clean) return;
        set((s) => ({
          name: clean,
          pronouns: persona.pronouns || s.pronouns,
        }));
        useTeamStore.setState({ assistantName: displayAssistantName(clean) });
      },

      resetDefaults: () => {
        set({
          name: "Maya",
          tagline: "Your AI SDR",
          pronouns: "she/her",
          avatarKind: "gradient",
          avatarLabel: null,
          avatarPreviewUrl: null,
          avatarPendingClear: false,
          samples: { ...DEFAULT_SAMPLES },
          tones: { ...DEFAULT_TONES },
          signatures: { ...DEFAULT_SIGNATURES },
        });
        useTeamStore.setState({ assistantName: "Maya" });
      },
    }),
    {
      name: "sellix.persona.v1",
      // Never persist avatar binary/data URLs — backend is the source of truth.
      partialize: (state) => ({
        name: state.name,
        tagline: state.tagline,
        pronouns: state.pronouns,
        samples: state.samples,
        tones: state.tones,
        signatures: state.signatures,
      }),
    },
  ),
);

// Sample-output bank — keyed by direct/diplomatic + concise/detailed bucket
// Each variant has 2 rolls so the Refresh button has something to pick from.
export const SAMPLE_BANK = {
  whatsapp: [
    { variant: "direct-concise",     text: "Hey Sarah — saw your post on 9-step sequences. I have a Ramp benchmark that might land. Worth 30 seconds?" },
    { variant: "direct-detailed",    text: "Hey Sarah — your post on 9-step sequences landed for me. We pulled a similar pattern at Ramp and the response curve flattened after step 4. Happy to share the data if useful — 30 seconds?" },
    { variant: "diplomatic-concise", text: "Hi Sarah! Loved your sequences post. We ran something similar at Ramp — would you have 30 seconds to compare notes?" },
    { variant: "diplomatic-detailed",text: "Hi Sarah — really enjoyed your perspective on sequence length. We tested a similar approach at Ramp last quarter and learned a few counterintuitive things about reply velocity. If you've got 30 seconds I'd love to swap notes." },
  ],
  pricing: [
    { variant: "direct-concise",     text: "$2k/mo, 100 seats included. Custom is open if you need more. Free 14-day trial — no card required." },
    { variant: "direct-detailed",    text: "Our Founder + Team plan is $999/mo and covers up to 5 collaborators. Above that we move to Custom — typically $2-3k/mo for 50+ seats. You can run the full product for 14 days with no card." },
    { variant: "diplomatic-concise", text: "Happy to share — $999/mo for the Founder + Team plan, custom pricing above 5 seats. We always start with a free 14-day trial." },
    { variant: "diplomatic-detailed",text: "Of course — pricing depends on team size. Founder + Team is $999/mo for up to 5 people, and we move to custom above that (most teams land around $2-3k/mo for 50 seats). We always recommend the free 14-day trial first so you can see real outputs before you commit." },
  ],
  linkedinConnect: [
    { variant: "direct-concise",     text: "Hey — saw your work on RevOps benchmarks. Would love to compare notes. No agenda." },
    { variant: "direct-detailed",    text: "Hey — your RevOps benchmarks piece kept me up last night (the multi-touch attribution section especially). Would love to compare notes. Open to a Zoom or just async DMs — your call." },
    { variant: "diplomatic-concise", text: "Hi! Your RevOps benchmarks work has been so useful for our team. Would love to connect." },
    { variant: "diplomatic-detailed",text: "Hi — your RevOps benchmarks work has been one of the most useful things our team has read this year. Would love to connect, swap notes on attribution, and stay in your orbit." },
  ],
  emailFollowup: [
    { variant: "direct-concise",     text: "Sarah — circling back on this. Should I take this off your plate or is it still useful?" },
    { variant: "direct-detailed",    text: "Sarah — circling back on the Ramp benchmark thread. Totally fine if priorities shifted; I just want to make sure I'm not leaving you on read. Should I park this or is it still useful?" },
    { variant: "diplomatic-concise", text: "Hi Sarah — just floating this back to the top in case it got buried. No urgency." },
    { variant: "diplomatic-detailed",text: "Hi Sarah — just floating this back to the top of your inbox in case it got buried. No urgency at all — if the timing's wrong I can park it and check back in a few weeks." },
  ],
};

// Bucket the current tone settings into one of the 4 variants
export function bucketForTones(tones) {
  const direct = tones.directDiplomatic < 50 ? "direct" : "diplomatic";
  const concise = tones.conciseDetailed < 50 ? "concise" : "detailed";
  return `${direct}-${concise}`;
}
